import { useApolloClient } from '@apollo/client'
import { FC, useContext, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  AUTH_LINKS,
  AUTH_TOKEN,
  HELMER_KEYS,
  TIMER_PAUSED_FOR_SESSION_END,
  USER_EMAIL,
  sessionEndingMessage
} from '../../constants'
import { AuthContext } from '../../context/AuthContext'
import { useTimerControls } from '../../context/TimerContext'
import {
  LoginRouteState,
  SESSION_ENDED_EVENT,
  SESSION_END_REASON,
  SessionEndReason,
  SessionEndedDetail,
  endSession,
  getIsTimerRunning,
  getStoredToken,
  isPublicPath,
  msUntilExpiry,
  sessionLifetime
} from '../../utils'
import { Alert } from '../common/Alert'

// The longest delay setTimeout honours; a longer session is waited out in steps.
const MAX_TIMER_DELAY = 2 ** 31 - 1

const MINUTE = 60_000

// How long before expiry the user is warned. A lead as long as the whole session is skipped,
// so a short session (e.g. a 2m one in QA) is not warned about the moment it starts.
const WARNING_LEADS = [10 * MINUTE, MINUTE]

// Pausing the timer is an API call, so it has to happen while the session still works.
const TIMER_PAUSE_LEAD = MINUTE

// The warnings stay up until dismissed; this key takes them down once the session is over.
const SESSION_ENDING_TOAST = 'session-ending'

/**
 * Pauses the running timer before the session can no longer save it. The pause is a toggle
 * on the API, so with several tabs open only one may send it: the lock serialises them and
 * the stored timer state, written by whichever went first, tells the rest it is done.
 */
const pauseRunningTimer = (pauseTimer: (entryId: string) => void) => {
  const pause = () => {
    const { entryId } = JSON.parse(localStorage.getItem(HELMER_KEYS.FORM) || '{}')
    if (!getIsTimerRunning() || !entryId) return

    pauseTimer(entryId)
    Alert.info(TIMER_PAUSED_FOR_SESSION_END, { persist: true })
  }

  if (!navigator.locks) return pause()
  navigator.locks.request('helmer-session-end-timer-pause', async () => pause())
}

/** Why the session ended, as `endSession` recorded it; a session that just vanished was signed out. */
const storedEndReason = () =>
  (localStorage.getItem(SESSION_END_REASON) as SessionEndReason | null) ?? SessionEndReason.SIGNED_OUT

/**
 * Owns the end of a session: ends it when the token's expiry arrives, follows other tabs
 * when they end it, and turns every `endSession` call into a reset of the signed-in state
 * plus a trip to the login page, remembering where the user was.
 */
export const SessionWatcher: FC = () => {
  const { isLoggedIn, currentUser, setUser, setIsLoggedIn } = useContext(AuthContext)
  const { pauseTimer } = useTimerControls()
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const location = useLocation()

  // Read inside the listeners, which are subscribed once. Before the user has loaded, e.g. on a
  // reload after expiry, the email stored at sign-in says whose session it was.
  const email = currentUser?.email ?? localStorage.getItem(USER_EMAIL)
  const latest = useRef({ location, isLoggedIn, email })
  latest.current = { location, isLoggedIn, email }

  useEffect(() => {
    const leave = (reason: SessionEndReason) => {
      const { location: from, email } = latest.current

      localStorage.removeItem(USER_EMAIL)
      Alert.close(SESSION_ENDING_TOAST)
      setUser(null)
      setIsLoggedIn(false)

      if (isPublicPath(from.pathname)) return

      const state: LoginRouteState = { from, sessionEnded: reason, email: email ?? undefined }
      navigate(AUTH_LINKS.LOGIN_LINK, { replace: true, state })
    }

    const onSessionEnded = (event: Event) => leave((event as CustomEvent<SessionEndedDetail>).detail.reason)

    // Another tab signed out, or saw the session end first. Storage is shared, so all that
    // is left to do here is this tab's own state.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_TOKEN && event.key !== null) return
      if (getStoredToken() || !latest.current.isLoggedIn) return

      apolloClient.clearStore()
      leave(storedEndReason())
    }

    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded)
    window.addEventListener('storage', onStorage)

    // The session can also end while this is not mounted: AuthContext swaps the router for its
    // loader while the user loads, which is exactly when a refused token comes back. Catch up.
    if (latest.current.isLoggedIn && !getStoredToken()) leave(storedEndReason())

    return () => {
      window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded)
      window.removeEventListener('storage', onStorage)
    }
    // Subscribed once: the setters only wrap stable state setters, the login path is
    // absolute, and everything that changes is read through `latest`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    let warnedToken: string | null = null
    let warned = new Set<number>()

    // Reads the stored token every time, so a token another tab stored meanwhile is the one timed.
    const check = () => {
      const token = getStoredToken()
      const remaining = token ? msUntilExpiry(token) : null

      if (!token || remaining === null) return
      if (remaining <= 0) return endSession(SessionEndReason.EXPIRED)

      if (token !== warnedToken) {
        warnedToken = token
        warned = new Set()
      }

      const lifetime = sessionLifetime(token) ?? Infinity
      const pending = WARNING_LEADS.filter(lead => lead < lifetime && !warned.has(lead))
      const due = pending.filter(lead => remaining <= lead)

      // One warning however many are due at once, e.g. on opening the app a minute from the end.
      if (due.length) {
        due.forEach(lead => warned.add(lead))
        // Replaces the earlier warning, which would otherwise still be up with the old minutes.
        Alert.close(SESSION_ENDING_TOAST)
        Alert.warning(sessionEndingMessage(Math.ceil(remaining / MINUTE)), {
          persist: true,
          key: SESSION_ENDING_TOAST
        })
      }

      if (remaining <= TIMER_PAUSE_LEAD) pauseRunningTimer(pauseTimer)

      // Wake for the next warning, the timer pause, or the end, whichever comes first.
      const nextWake = Math.min(
        remaining,
        ...[...pending.filter(lead => !due.includes(lead)), TIMER_PAUSE_LEAD]
          .filter(lead => remaining > lead)
          .map(lead => remaining - lead)
      )
      timer = setTimeout(check, Math.min(nextWake, MAX_TIMER_DELAY))
    }

    // Background tabs and sleeping machines hold timers back, so look again on return.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      clearTimeout(timer)
      check()
    }

    check()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isLoggedIn, pauseTimer])

  return null
}
